import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { FaFlask, FaVial, FaFileMedicalAlt } from "react-icons/fa";
import { baseurImage } from "../../Baseurl";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function PortalLabs({ patientId }) {
    const [labData, setLabData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labs, setLabs] = useState([]);

    const [serviceListModal, setServiceListModal] = useState(false);
    const [selectedServicesForModal, setSelectedServicesForModal] = useState([]);

    useEffect(() => {
        const getLabsbypatient = async () => {
            try {
                const response = await axiosInstance.get(
                    `getLabRequestsByPatient/${patientId}`
                );
                if (response.data.success === true) {
                    setLabData(response.data.data);
                } else {
                    console.error(
                        "Something went wrong:",
                        response.data.message || response.data
                    );
                }
            } catch (error) {
                console.error("API Error:", error.response?.data || error.message);
            }
        };

        if (patientId) {
            getLabsbypatient();
        }
    }, [patientId]);

    const handleSeeMoreServices = (services) => {
        setSelectedServicesForModal(services || []);
        setServiceListModal(true);
    };

    const getTestStatus = (value, minRange, maxRange) => {
        const v = parseFloat(value);
        if (isNaN(v)) return "no-range";
        const min = minRange !== null && minRange !== undefined ? parseFloat(minRange) : null;
        const max = maxRange !== null && maxRange !== undefined ? parseFloat(maxRange) : null;
        if (min === null && max === null) return "no-range";
        if (min !== null && v < min) return "below";
        if (max !== null && v > max) return "above";
        return "normal";
    };

    const handleGenerateReport = async (lab) => {
        try {
            const [logoRes, valuesRes] = await Promise.all([
                axiosInstance.get("getLogo"),
                axiosInstance.get(`getLabReportValues/${lab.request_id}`),
            ]);

            const logoDataUrl = logoRes.data?.success ? logoRes.data.logo : null;
            const testValues = valuesRes.data?.success ? (valuesRes.data.data || []) : [];

            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;
            const contentWidth = pageWidth - margin * 2;
            let y = 15;

            const fmtDate = (val) => {
                if (!val) return "—";
                return new Date(val).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
            };

            // ── Logo ──
            if (logoDataUrl) {
                try {
                    const mimeMatch = logoDataUrl.match(/^data:image\/(\w+);base64,/);
                    const imgFormat = mimeMatch ? mimeMatch[1].toUpperCase().replace("JPG", "JPEG") : "PNG";
                    const imgProps = doc.getImageProperties(logoDataUrl);
                    const maxW = 50, maxH = 25;
                    const ratio = Math.min(maxW / imgProps.width, maxH / imgProps.height);
                    const imgW = imgProps.width * ratio;
                    const imgH = imgProps.height * ratio;
                    doc.addImage(logoDataUrl, imgFormat, (pageWidth - imgW) / 2, y, imgW, imgH);
                    y += imgH + 5;
                } catch (imgErr) {
                    console.warn("Logo render failed:", imgErr);
                }
            }

            // ── Title ──
            doc.setDrawColor(0);
            doc.setLineWidth(0.5);
            doc.line(margin, y, pageWidth - margin, y);
            y += 6;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(0);
            doc.text("LAB TEST REPORT", pageWidth / 2, y, { align: "center" });
            y += 3;
            doc.line(margin, y, pageWidth - margin, y);
            y += 7;

            // ── Patient Info ──
            doc.setFontSize(9.5);
            const col1x = margin;
            const col2x = pageWidth / 2 + 5;
            const labelW = 38;

            const infoLeft = [
                ["Patient Name", lab.patient_name || "—"],
                ["Age / Gender", [lab.age ? `${lab.age} yrs` : null, lab.gender].filter(Boolean).join(" / ") || "—"],
                ["CPR (Civil ID)", lab.patient_civil_id || "—"],
            ];
            const infoRight = [
                ["Sample Collected At", lab.lab_name || "—"],
            ];

            const infoRowCount = Math.max(infoLeft.length, infoRight.length);
            for (let i = 0; i < infoRowCount; i++) {
                let rowHeight = 6;
                if (infoLeft[i]) {
                    const [label, value] = infoLeft[i];
                    doc.setFont("helvetica", "bold");
                    doc.text(`${label}:`, col1x, y);
                    doc.setFont("helvetica", "normal");
                    if (label === "Patient Name" && value.length > 20) {
                        const lines = [];
                        for (let s = 0; s < value.length; s += 20) lines.push(value.slice(s, s + 20));
                        doc.text(lines, col1x + labelW, y);
                        rowHeight = Math.max(rowHeight, 6 + (lines.length - 1) * 5);
                    } else {
                        doc.text(value, col1x + labelW, y);
                    }
                }
                if (infoRight[i]) {
                    doc.setFont("helvetica", "bold");
                    doc.text(`${infoRight[i][0]}:`, col2x, y);
                    doc.setFont("helvetica", "normal");
                    doc.text(infoRight[i][1], col2x + labelW, y);
                }
                y += rowHeight;
            }
            y += 4;

            // ── Lab Test Results Table ──
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text("Test Results", margin, y);
            y += 4;

            if (testValues.length > 0) {
                const tableBody = testValues.map((row) => {
                    const isText = row.value_type === "text";
                    const status = isText ? null : getTestStatus(row.value, row.min_range, row.max_range);
                    const isAbnormal = status === "above" || status === "below";
                    let valStr;
                    if (isText) {
                        valStr = row.value !== null && row.value !== undefined && row.value !== "" ? String(row.value) : "—";
                    } else {
                        valStr = row.value !== null && row.value !== undefined && row.value !== "" ? parseFloat(row.value).toFixed(2) : "—";
                    }
                    let refRange;
                    if (isText) {
                        refRange = row.reference_text || "—";
                    } else {
                        const minStr = row.min_range !== null && row.min_range !== undefined ? parseFloat(row.min_range).toFixed(2) : "—";
                        const maxStr = row.max_range !== null && row.max_range !== undefined ? parseFloat(row.max_range).toFixed(2) : "—";
                        refRange = (minStr !== "—" || maxStr !== "—") ? `${minStr} – ${maxStr}` : "—";
                    }
                    return { cells: [row.test_name, valStr, row.units || "—", refRange, row.methodology || "—"], isAbnormal };
                });

                autoTable(doc, {
                    startY: y,
                    tableWidth: contentWidth,
                    margin: { left: margin, right: margin },
                    head: [["Test Name", "Value", "Units", "Ref. Range", "Methodology"]],
                    body: tableBody.map((r) => r.cells),
                    styles: { fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2, fillColor: [255, 255, 255] },
                    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold", lineWidth: 0.3, lineColor: [0, 0, 0] },
                    alternateRowStyles: { fillColor: [248, 248, 248] },
                    columnStyles: {
                        0: { cellWidth: 55 },
                        1: { cellWidth: 22, halign: "center" },
                        2: { cellWidth: 20, halign: "center" },
                        3: { cellWidth: 35, halign: "center" },
                        4: { cellWidth: 50 },
                    },
                    didParseCell: (data) => {
                        if (data.section === "body" && tableBody[data.row.index]?.isAbnormal) {
                            data.cell.styles.fontStyle = "bold";
                        }
                    },
                });
                y = (doc.lastAutoTable?.finalY ?? y) + 8;
            } else {
                doc.setFont("helvetica", "italic");
                doc.setFontSize(9.5);
                doc.setTextColor(80);
                doc.text("No lab test results recorded.", margin, y);
                doc.setTextColor(0);
                y += 10;
            }

            // ── Sample Dates ──
            doc.setDrawColor(0);
            doc.setLineWidth(0.2);
            doc.line(margin, y, pageWidth - margin, y);
            y += 5;

            const sampleDates = [
                ["Sample Collected On", fmtDate(lab.request_date)],
                ["Sample Received On",  fmtDate(lab.pending_at)],
                ["Sample Released On",  fmtDate(lab.received_at)],
            ];
            doc.setFontSize(9);
            const dateColW = contentWidth / 3;
            sampleDates.forEach(([label, val], i) => {
                const x = margin + i * dateColW;
                doc.setFont("helvetica", "bold");
                doc.text(label, x, y);
                doc.setFont("helvetica", "normal");
                doc.text(val, x, y + 5);
            });
            y += 14;

            // ── Footer ──
            doc.setDrawColor(0);
            doc.setLineWidth(0.3);
            doc.line(margin, y, pageWidth - margin, y);
            y += 5;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            doc.setTextColor(80);
            const now = new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
            doc.text(`Generated: ${now}`, margin, y);
            if (lab.result_added_by) {
                doc.setFont("helvetica", "bold");
                doc.text(`Done By: ${lab.result_added_by}`, pageWidth - margin, y, { align: "right" });
            }

            const pdfBlob = doc.output("blob");
            const blobUrl = URL.createObjectURL(pdfBlob);
            window.open(blobUrl, "_blank");
        } catch (err) {
            console.error("Generate report error:", err);
        }
    };

    const filteredLabs = labData;

    if (loading) return <div className="p-4 text-center">Loading lab reports...</div>;

    return (
        <div className="col-12">
            <div className="row">
                <div className="col-12 searchParent"></div>
                <div className="col-md-12">
                    <div className="card table-card patientCardHeader">
                        <div className="tableHeader">
                        </div>
                        <div className="pb-0">
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Services</th>
                                            <th>Date</th>
                                            <th>Attachments</th>
                                            <th>Report</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLabs.length > 0 ? (
                                            filteredLabs.map((item, index) => {
                                                return (
                                                    <>
                                                        <tr key={index}>
                                                            <td>
                                                                {item.services &&
                                                                    item.services.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {item.services
                                                                            .slice(0, 2)
                                                                            .map((service, i) => (
                                                                                <span
                                                                                    key={i}
                                                                                    className="me-1"
                                                                                >
                                                                                    {service.serviceName}
                                                                                    {i < item.services.slice(0, 2).length - 1 && ", "}
                                                                                </span>
                                                                            ))}
                                                                        {item.services.length > 2 && (
                                                                            <span
                                                                                className="badge bg-primary"
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                }}
                                                                                onClick={() =>
                                                                                    handleSeeMoreServices(
                                                                                        item.services
                                                                                    )
                                                                                }
                                                                            >
                                                                                +{item.services.length - 2}{" "}
                                                                                More
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </td>
                                                            <td>{item.request_date}</td>
                                                            <td>
                                                                {item.attachments &&
                                                                    item.attachments.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {item.attachments
                                                                            .slice(0, 2)
                                                                            .map((attachment, i) => (
                                                                                <a
                                                                                    key={i}
                                                                                    href={`${baseurImage}${attachment}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="badge bg-info text-white text-decoration-none"
                                                                                    title={`Click to open: ${attachment}`}
                                                                                    style={{ cursor: "pointer" }}
                                                                                >
                                                                                    <i className="ti ti-paperclip me-1"></i>
                                                                                    {attachment.length > 15
                                                                                        ? attachment.substring(0, 15) + "..."
                                                                                        : attachment}
                                                                                </a>
                                                                            ))}
                                                                        {item.attachments.length > 2 && (
                                                                            <span
                                                                                className="badge bg-secondary"
                                                                                style={{ cursor: "pointer" }}
                                                                                onClick={() => {
                                                                                    // Show all attachments in a simple alert or modal
                                                                                    const allFiles = item.attachments.map((att, idx) =>
                                                                                        `${idx + 1}. ${att}`
                                                                                    ).join('\n');
                                                                                    alert(`All Attachments:\n\n${allFiles}`);
                                                                                }}
                                                                            >
                                                                                +{item.attachments.length - 2}{" "}
                                                                                More
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">No attachments</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    style={{ fontSize: "0.78em", padding: "3px 10px", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", fontWeight: 600, borderRadius: "6px" }}
                                                                    onClick={() => handleGenerateReport(item)}
                                                                >
                                                                    <i className="ti ti-file-text me-1" />Generate Report
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    </>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center">
                                                    No request found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {serviceListModal && (
                            <div
                                className="modal fade show"
                                style={{
                                    backgroundColor: "rgba(0,0,0,0.5)",
                                    display: "block",
                                    zIndex: 1055,
                                }}
                            >
                                <div className="modal-dialog modal-md">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Lab Services</h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setServiceListModal(false)}
                                            ></button>
                                        </div>
                                        <div className="modal-body">
                                            <ul className="list-group">
                                                {selectedServicesForModal.map(
                                                    (service, index) => (
                                                        <li
                                                            key={index}
                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                        >
                                                            <div>
                                                                <strong>{service.serviceName}</strong>
                                                                {service.price && (
                                                                    <div className="text-success small">
                                                                        Price: {service.price} BHD
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {service.category && (
                                                                <span className="badge bg-secondary rounded-pill">
                                                                    {service.category}
                                                                </span>
                                                            )}
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => setServiceListModal(false)}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
